
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { User, ShoppingBag, MapPin, Phone, Mail, Clock, Package, ArrowRight } from 'lucide-react';
import { collection, query, where, getDocs, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { COLLECTIONS } from '../firebase/collections';
import NavBar from '../components/NavBar';

const UserProfile = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState('profile');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const latestOrder = orders[0];

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    setLoading(true);

    const q = query(
      collection(db, COLLECTIONS.ORDERS),
      where('customerEmail', '==', currentUser.email)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const userOrders = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => {
          const aTime = a.createdAt?.toDate?.()?.getTime?.() || 0;
          const bTime = b.createdAt?.toDate?.()?.getTime?.() || 0;
          return bTime - aTime;
        });
      setOrders(userOrders);
      setLoading(false);
    }, (error) => {
      console.error('Error listening to orders:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser, navigate]);

  useEffect(() => {
    // السماح بالتنقل للصفحة الرئيسية فقط
    if (currentPage === 'home') {
      navigate('/');
    }
  }, [currentPage, navigate]);

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'processing': return 'bg-blue-100 text-blue-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'completed': return 'مكتمل';
      case 'pending': return 'قيد الانتظار';
      case 'processing': return 'قيد المعالجة';
      case 'cancelled': return 'ملغي';
      default: return status;
    }
  };

  if (!currentUser) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <NavBar 
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      <div className="pt-24 px-4 pb-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-6">
            
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="text-center mb-6">
                  <div className="w-24 h-24 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <User className="w-12 h-12 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {currentUser.displayName || 'المستخدم'}
                  </h2>
                  <p className="text-gray-600 text-sm mt-1">{currentUser.email}</p>
                </div>

                <div className="space-y-4 border-t border-gray-200 pt-6">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-sm text-gray-500 mb-2">أحدث حالة للطلب</p>
                    {latestOrder ? (
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-gray-800">
                            طلب #{latestOrder.id.slice(0, 8)}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {latestOrder.createdAt?.toDate?.()?.toLocaleString('ar-EG') || 'تاريخ غير متاح'}
                          </p>
                        </div>
                        <span className={`px - 3 py - 1 rounded - full text - xs font - medium ${ getStatusColor(latestOrder.status) } `}>
                          {getStatusText(latestOrder.status)}
                        </span>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-600">لا يوجد طلبات بعد.</p>
                    )}
                  </div>

                  <div className="flex items-center gap-3 text-gray-700">
                    <Mail className="w-5 h-5 text-yellow-600" />
                    <div>
                      <p className="text-sm text-gray-500">البريد الإلكتروني</p>
                      <p className="font-medium">{currentUser.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 text-gray-700">
                    <ShoppingBag className="w-5 h-5 text-yellow-600" />
                    <div>
                      <p className="text-sm text-gray-500">إجمالي الطلبات</p>
                      <p className="font-medium">{orders.length} طلب</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 text-gray-700">
                    <Clock className="w-5 h-5 text-yellow-600" />
                    <div>
                      <p className="text-sm text-gray-500">عضو منذ</p>
                      <p className="font-medium">
                        {currentUser.metadata?.creationTime ? 
                          new Date(currentUser.metadata.creationTime).toLocaleDateString('ar-EG') : 
                          'غير محدد'}
                      </p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => navigate('/')}
                  className="w-full mt-6 bg-yellow-500 hover:bg-yellow-600 text-white py-3 rounded-lg font-bold transition-colors flex items-center justify-center gap-2"
                >
                  العودة للموقع
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">طلباتي</h3>

                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
                  </div>
                ) : orders.length > 0 ? (
                  <div className="space-y-4">
                    {orders.map((order) => (
                      <div key={order.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                              <Package className="w-6 h-6 text-yellow-600" />
                              <div>
                                <h4 className="font-bold text-gray-900">
                                  طلب #{order.id.slice(0, 8)}
                                </h4>
                                <p className="text-sm text-gray-500">
                                  {order.createdAt?.toDate?.()?.toLocaleDateString('ar-EG')}
                                </p>
                              </div>
                            </div>
                            
                            {order.productDetails && (
                              <p className="text-gray-700 mb-2">
                                <span className="font-medium">المنتج:</span> {order.productDetails.name}
                              </p>
                            )}
                            
                            <p className="text-gray-700 mb-2">
                              <span className="font-medium">النوع:</span> {order.type || 'عام'}
                            </p>
                            
                            {order.notes && (
                              <p className="text-gray-600 text-sm">
                                <span className="font-medium">ملاحظات:</span> {order.notes}
                              </p>
                            )}
                          </div>

                          <div className="flex flex-col items-end gap-3">
                            <span className={`px - 4 py - 2 rounded - full text - sm font - medium ${ getStatusColor(order.status) } `}>
                              {getStatusText(order.status)}
                            </span>
                            
                            {order.total && (
                              <p className="text-2xl font-bold text-yellow-600">
                                ${order.total}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <ShoppingBag className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg">لا توجد طلبات حالياً</p>
                    <button
                      onClick={() => navigate('/')}
                      className="mt-4 bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                    >
                      تصفح المنتجات
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
